// PersistMiddlewareLoader.jsx
import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { persist as persistDebug } from '../resource/debug.js';
import { NULL_FUNCTION } from '../resource/defaults.js';
import { hasWebviewCallHandler } from '../resource/jsBridgeInterface.js';

const log = persistDebug.extend('log:PersistMiddlewareLoader');
const realUserEvents = ['mousemove', 'click', 'scroll'];

export const PersistMiddlewareLoader = ({
  userId = '',
  refreshToken = '',
  restoreRedux = NULL_FUNCTION,
  persistNow = NULL_FUNCTION,
  postMessageToSwagApp = NULL_FUNCTION,
}) => {
  const userIdRef = useRef(userId);
  const isAttachedRef = useRef(false);
  const prevRefreshToken = useRef(refreshToken);

  const attachListeners = () => {
    if (!isAttachedRef.current) {
      isAttachedRef.current = true;
      document.addEventListener('visibilitychange', visibilityChangeHandler);
      window.addEventListener('pageshow', pageshowHandler);
      window.addEventListener('pagehide', pagehideHandler);
      window.addEventListener('focus', focusHandler);
      window.addEventListener('blur', blurHandler);
      log('attach listeners');
    }
  };

  const detachListeners = () => {
    document.removeEventListener('visibilitychange', visibilityChangeHandler);
    window.removeEventListener('pageshow', pageshowHandler);
    window.removeEventListener('pagehide', pagehideHandler);
    window.removeEventListener('focus', focusHandler);
    window.removeEventListener('blur', blurHandler);
    log('detach listeners');
  };

  const visibilityChangeHandler = async () => {
    log('visibilitychange', document.visibilityState);
    if (document.visibilityState === 'visible') {
      await restoreReduxFromIndexedDB();
      addPersistMiddleware();
    } else removePersistMiddleware();
  };

  const pageshowHandler = async () => {
    if (!document.hidden) {
      log('page show');
      await restoreReduxFromIndexedDB();
      addPersistMiddleware();
    }
  };

  const pagehideHandler = async () => {
    if (document.hidden) {
      log('page hide');
      removePersistMiddleware();
    }
  };

  const focusHandler = async () => {
    log('on focus');
    await restoreReduxFromIndexedDB();
    addPersistMiddleware();
  };

  const blurHandler = async () => {
    log('on blur');
    removePersistMiddleware();
  };

  const restoreReduxFromIndexedDB = async () => {
    log('restore redux from indexedDB');

    const { loadState } = await import('../resource/persist.js');
    const storageState = await loadState();
    await restoreRedux(storageState);
  };

  const addPersistMiddleware = async () => {
    const [{ addMiddleware, hasMiddlewareExisted }, { persistMiddleware }] =
      await Promise.all([
        import('../middleware/dynamicMiddleware.js'),
        import('../middleware/persist.js'),
      ]);
    if (!hasMiddlewareExisted(persistMiddleware)) {
      addMiddleware(persistMiddleware);
      log('add persist middleware');
    } else log('already loaded');
  };

  const removePersistMiddleware = async () => {
    const [{ removeMiddleware, hasMiddlewareExisted }, { persistMiddleware }] =
      await Promise.all([
        import('../middleware/dynamicMiddleware.js'),
        import('../middleware/persist.js'),
      ]);

    if (hasMiddlewareExisted(persistMiddleware)) {
      removeMiddleware(persistMiddleware);
      log('remove persist middleware');
    } else log('already unloaded');
  };

  const syncPersist = async () => {
    log('syncPersist');
    await persistNow();
    log('persist complete');

    const [{ sendMessageToSW }, { RESTORE_REDUX }] = await Promise.all([
      import('../serviceWorker/helpers.js'),
      import('../serviceWorker/AppMessageTypes.js'),
    ]);
    sendMessageToSW({ type: RESTORE_REDUX });
    log('send message to sw to sync persist');
  };

  const initPersistMiddleware = () => {
    log('init persist middleware');
    realUserEvents.forEach(event => {
      window.removeEventListener(event, initPersistMiddleware);
    });

    requestIdleCallback(async () => {
      await restoreReduxFromIndexedDB();
      await addPersistMiddleware();
      attachListeners();
    });
  };

  const syncRefreshTokenForWebview = async () => {
    if (hasWebviewCallHandler()) {
      await persistNow();
      postMessageToSwagApp({
        message: 'onRefreshTokenUpdate',
        messageArgs: [refreshToken || null],
      });
    }
  };

  useEffect(() => {
    realUserEvents.forEach(event => {
      window.addEventListener(event, initPersistMiddleware);
    });
    return () => {
      isAttachedRef.current = false;
      detachListeners();
      realUserEvents.forEach(event => {
        window.removeEventListener(event, initPersistMiddleware);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // login
    if (!userIdRef.current && userId) {
      // The tab that does the login action should set the shouldSyncPersist flag,
      // so that other tabs won't need to do syncPersist after restoring redux
      // sync persist when there is no shouldSyncPersist flag in the begging or shouldSyncPersist flag is 'true'
      const shouldSyncPersist = localStorage.getItem('shouldSyncPersist');
      if (shouldSyncPersist === null || shouldSyncPersist === 'true') {
        log('syncPersist because the userId is set');
        syncPersist();
        localStorage.setItem('shouldSyncPersist', false);
      }
    }

    // logout
    if (userIdRef.current && !userId) {
      // set shouldSyncPersist to true for next login action
      localStorage.setItem('shouldSyncPersist', true);
    }

    userIdRef.current = userId;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    if (prevRefreshToken.current !== refreshToken) {
      prevRefreshToken.current = refreshToken;
      syncRefreshTokenForWebview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  return null;
};

PersistMiddlewareLoader.propTypes = {
  userId: PropTypes.string,
  refreshToken: PropTypes.string,
  restoreRedux: PropTypes.func,
  persistNow: PropTypes.func,
  postMessageToSwagApp: PropTypes.func,
};

export default PersistMiddlewareLoader;
